/**
 * Enhanced Date Picker Component
 * 
 * A comprehensive date picker with Brazilian business rules validation,
 * timezone handling, and enhanced user experience features.
 * 
 * @module components/ui/date-picker
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { CalendarIcon, AlertCircle, Check } from 'lucide-react';
import { format, addMonths, subMonths, isToday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';

// Enhanced date utilities
import {
  validateSaleDate,
  DateValidationResult,
  BusinessDateRules,
  toBrazilTimezone,
  fromDateString,
  isBrazilianHoliday,
  isBrazilianBusinessDay,
  getQuickDateOptions,
  QuickDateOption,
  formatDateWithTimezone,
  formatDateBR,
} from '@/lib/utils/dateUtils';

/**
 * Enhanced date picker props
 */
export interface EnhancedDatePickerProps {
  /** Currently selected date */
  value?: Date | string | null;
  /** Callback when date changes */
  onChange?: (date: Date | null) => void;
  /** Placeholder text when no date is selected */
  placeholder?: string;
  /** Whether the picker is disabled */
  disabled?: boolean;
  /** Business rules to apply for validation */
  businessRules?: BusinessDateRules;
  /** Whether to show quick date selection shortcuts */
  showQuickOptions?: boolean;
  /** Whether to show validation feedback */
  showValidation?: boolean;
  /** Custom validation handler */
  onValidationChange?: (result: DateValidationResult) => void;
  /** Custom class names */
  className?: string;
  /** Additional CSS for the button */
  buttonClassName?: string;
  /** Whether to display timezone information */
  showTimezone?: boolean;
  /** Accessibility label for the button */
  ariaLabel?: string;
  /** Custom date formatting function */
  formatDate?: (date: Date) => string;
}



/**
 * Enhanced date picker component with Brazilian business rules
 */
export function EnhancedDatePicker({
  value,
  onChange,
  placeholder = 'Selecione uma data',
  disabled = false,
  businessRules,
  showQuickOptions = true,
  showValidation = true,
  onValidationChange,
  className,
  buttonClassName,
  showTimezone = false,
  ariaLabel = 'Selecionar data',
  formatDate,
}: EnhancedDatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [validationResult, setValidationResult] = useState<DateValidationResult | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);

  // Convert string to Date object if needed
  const selectedDate = React.useMemo(() => {
    if (!value) return null;
    return fromDateString(value);
  }, [value]);

  // Validation effect
  useEffect(() => {
    if (selectedDate && showValidation) {
      const result = validateSaleDate(selectedDate, businessRules);
      setValidationResult(result);
      onValidationChange?.(result);
    } else {
      setValidationResult(null);
    }
  }, [selectedDate, businessRules, showValidation, onValidationChange]);

  // Handle date selection
  const handleSelect = useCallback((date: Date | undefined) => {
    if (!date) {
      onChange?.(null);
      setIsOpen(false);
      return;
    }

    // Validate the selected date
    const validation = validateSaleDate(date, businessRules);
    
    if (validation.isValid || !showValidation) {
      // Convert to Brazil timezone before returning
      const brazilDate = toBrazilTimezone(date);
      onChange?.(brazilDate);
      setIsOpen(false);
    } else {
      // Show validation feedback but allow selection with warning
      setValidationResult(validation);
      onValidationChange?.(validation);
      
      // Allow selection but show warning
      const brazilDate = toBrazilTimezone(date);
      onChange?.(brazilDate);
    }
  }, [onChange, businessRules, showValidation, onValidationChange]);

  // Quick date options
  const quickOptions = React.useMemo(() => {
    return getQuickDateOptions(businessRules);
  }, [businessRules]);

  // Handle quick option selection
  const handleQuickOptionSelect = useCallback((option: QuickDateOption) => {
    if (option.isAvailable()) {
      const date = option.getDate();
      const brazilDate = toBrazilTimezone(date);
      onChange?.(brazilDate);
      setIsOpen(false);
    }
  }, [onChange]);

  // Keyboard navigation
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (disabled) return;

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        setIsOpen(!isOpen);
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      case 'ArrowDown':
        if (!isOpen) {
          event.preventDefault();
          setIsOpen(true);
        }
        break;
    }
  }, [disabled, isOpen]);

  // Format selected date for display
  const formatSelectedDate = useCallback((date: Date): string => {
    if (formatDate) {
      return formatDate(date);
    }

    if (showTimezone) {
      return formatDateWithTimezone(date);
    }

    // Use Brazilian formatting function
    return formatDateBR(date);
  }, [formatDate, showTimezone]);

  // Get status color for validation
  const getValidationStatusColor = useCallback(() => {
    if (!validationResult) return '';
    
    switch (validationResult.code) {
      case 'FUTURE_DATE':
      case 'TOO_OLD':
      case 'OUTSIDE_FISCAL_YEAR':
        return 'border-red-500 focus:border-red-500';
      case 'WEEKEND':
      case 'HOLIDAY':
        return 'border-yellow-500 focus:border-yellow-500';
      default:
        return validationResult.isValid ? 'border-green-500 focus:border-green-500' : '';
    }
  }, [validationResult]);

  // Focus management
  const handleOpenChange = useCallback((open: boolean) => {
    if (!disabled) {
      setIsOpen(open);
      if (open && selectedDate) {
        setCurrentMonth(selectedDate);
      }
    }
  }, [disabled, selectedDate]);

  return (
    <div className={cn('relative w-full', className)}>
      <Popover open={isOpen && !disabled} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'w-full justify-start text-left font-normal h-10 px-3',
              getValidationStatusColor(),
              disabled && 'opacity-50 cursor-not-allowed',
              buttonClassName
            )}
            disabled={disabled}
            onKeyDown={handleKeyDown}
            onClick={(e) => {
              // Prevent default only if disabled to avoid interfering with PopoverTrigger
              if (disabled) {
                e.preventDefault();
              }
            }}
            aria-label={ariaLabel}
            aria-expanded={isOpen}
            aria-haspopup="dialog"
            type="button"
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {selectedDate ? (
              <span className="truncate">
                {formatSelectedDate(selectedDate)}
              </span>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </Button>
        </PopoverTrigger>
        
        <PopoverContent 
          className="w-auto p-4" 
          align="start"
          side="bottom"
          sideOffset={4}
        >
          <div className="space-y-4">
            {/* Quick Options */}
            {showQuickOptions && (
              <div className="space-y-2">
                <div className="text-sm font-medium">Seleção Rápida</div>
                <div className="grid grid-cols-2 gap-2">
                  {quickOptions.map((option) => (
                    <Button
                      key={option.id}
                      variant="outline"
                      size="sm"
                      className={cn(
                        'h-8 text-xs',
                        !option.isAvailable() && 'opacity-50 cursor-not-allowed',
                        option.isAvailable() && 'hover:bg-primary hover:text-primary-foreground'
                      )}
                      onClick={() => handleQuickOptionSelect(option)}
                      disabled={!option.isAvailable()}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Simple date input */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Digite a data:
              </label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background text-foreground"
                value={selectedDate ? selectedDate.toISOString().split('T')[0] : ''}
                onChange={(e) => {
                  if (e.target.value) {
                    const date = new Date(e.target.value);
                    handleSelect(date);
                  } else {
                    handleSelect(undefined);
                  }
                }}
                disabled={disabled}
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Validation Feedback */}
      {showValidation && validationResult && !validationResult.isValid && (
        <div className="mt-1 flex items-center gap-1 text-xs">
          <AlertCircle className="h-3 w-3 text-orange-500" />
          <span className="text-orange-600">{validationResult.message}</span>
        </div>
      )}

      {/* Success Feedback */}
      {showValidation && validationResult?.isValid && selectedDate && (
        <div className="mt-1 flex items-center gap-1 text-xs">
          <Check className="h-3 w-3 text-green-500" />
          <span className="text-green-600">Data válida</span>
        </div>
      )}
    </div>
  );
}

/**
 * Default export for backward compatibility
 */
export default EnhancedDatePicker;
